import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Modal from "./Modal";
import Button from "./Button";
import ImagePicker from "./ImagePicker";
import DropZone from "./DropZone";
import MfaSettings from "./MfaSettings";
import { updateProfileApi, updatePictureApi } from "../../Redux/Users/User.actions";
import { useI18n } from "../../i18n/I18nContext";
import { apiErrorMessage } from "../../i18n/translate";
import styles from "./Styles/EditProfileModal.module.css";

const EditProfileModal = ({ show, onClose, user }) => {
  const { t, locale } = useI18n();
  const dispatch = useDispatch();
  const [name, setName] = useState(user.name || "");
  const [surname, setSurname] = useState(user.surname || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setName(user.name || "");
      setSurname(user.surname || "");
      setPassword("");
      setConfirmPassword("");
      setError("");
    }
  }, [show, user.name, user.surname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !surname) {
      setError(t("profile.pleaseAddNames"));
      return;
    }
    if (password && password !== confirmPassword) {
      setError(t("profile.passwordsDoNotMatch"));
      return;
    }
    try {
      await dispatch(updateProfileApi(name, surname, password, confirmPassword, user.token));
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, locale));
    }
  };

  return (
    <Modal show={show} title={t("profile.editProfileTitle")} onClose={onClose}>
      <div className={styles.avatarRow}>
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className={styles.avatar}
            style={{ objectPosition: `${user.pictureX ?? 50}% ${user.pictureY ?? 50}%` }}
          />
        ) : (
          <div className={styles.avatarPlaceholder} />
        )}
        <ImagePicker
          onChange={({ image, x, y }) => dispatch(updatePictureApi(image, x, y, user.token))}
        >
          {({ onPick, onFile }) => (
            <DropZone onFile={onFile}>
              <label className={styles.avatarLabel} title={t("common.uploadNewPicture")}>
                {t("common.updateAvatar")}
                <input type="file" accept="image/*" hidden onChange={onPick} />
              </label>
            </DropZone>
          )}
        </ImagePicker>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cw-field">
          <label className="cw-label">{t("common.firstName")}</label>
          <input
            className="cw-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            title={t("common.firstName")}
            autoFocus
          />
        </div>
        <div className="cw-field">
          <label className="cw-label">{t("common.lastName")}</label>
          <input
            className="cw-input"
            type="text"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            title={t("common.lastName")}
          />
        </div>
        <div className="cw-field">
          <label className="cw-label">{t("common.newPassword")}</label>
          <input
            className="cw-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("common.leaveBlankPassword")}
            title={t("common.leaveBlankPassword")}
          />
        </div>
        <div className="cw-field">
          <label className="cw-label">{t("common.confirmPassword")}</label>
          <input
            className="cw-input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            title={t("common.confirmPassword")}
          />
        </div>
        {password && confirmPassword && password === confirmPassword && (
          <p className="cw-success">{t("profile.passwordsMatch")}</p>
        )}
        {error && <p className="cw-error">{error}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button type="button" variant="secondary" onClick={onClose} title={t("common.discardChanges")}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" title={t("profile.saveProfile")}>
            {t("common.save")}
          </Button>
        </div>
      </form>

      {show && <MfaSettings token={user.token} />}
    </Modal>
  );
};

export default EditProfileModal;
